import React from 'react';
import { Form, Input, Button, DatePicker, Select, InputNumber, Modal } from 'antd';
import dayjs from 'dayjs';
import apiClient from '../services/api';
import { message } from 'antd';
import SupplierModal from './SupplierModal';

interface MaintenanceType {
  id: number;
  name: string;
}

interface AssetWorkRecordFormProps {
  assetId: number;
  asset?: any; // Thêm asset để lấy company_id
  maintenanceTypes: MaintenanceType[];
  suppliers: import('../types').Supplier[];
  employees: import('../types').Employee[];
  onSuccess: () => void;
  onCancel: () => void;
  onSupplierCreated?: () => void;
  editMode?: boolean; // Chế độ chỉnh sửa
  initialData?: import('../types').AssetWorkRecord; // Dữ liệu ban đầu khi chỉnh sửa
}

const AssetWorkRecordForm: React.FC<AssetWorkRecordFormProps> = ({ 
  assetId, 
  asset,
  maintenanceTypes, 
  suppliers, 
  employees, 
  onSuccess, 
  onCancel,
  onSupplierCreated,
  editMode = false,
  initialData
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [supplierModalVisible, setSupplierModalVisible] = React.useState(false);
  const [currentSupplierList, setCurrentSupplierList] = React.useState(suppliers);

  // Lọc employees theo company_id của asset để chỉ hiển thị nhân viên của công ty mà tài sản thuộc về
  const filteredEmployees = React.useMemo(() => {
    if (!asset?.company_id) return employees;
    return employees.filter(emp => emp.company_id === asset.company_id);
  }, [employees, asset?.company_id]);


  const handleSupplierCreated = (newSupplier: any) => {
    setCurrentSupplierList(prev => [...prev, newSupplier]);
    setSupplierModalVisible(false);
    // Tự động chọn NCC mới tạo
    form.setFieldsValue({ performed_by_supplier_id: newSupplier.id });
    if (onSupplierCreated) {
      onSupplierCreated();
    }
  };

  const handleFinish = async (values: any) => {
    setLoading(true);
    try {
      const workRecordData = {
        asset_id: assetId,
        maintenance_type_id: values.maintenance_type_id,
        work_description: values.work_description,
        notes: values.notes,
        work_date: values.completion_date.format('YYYY-MM-DD'), // Ngày tạo tác nghiệp
        completed_date: values.completion_date.format('YYYY-MM-DD'), // Ngày hoàn thành
        cost: Number(values.cost) || 0, // Sử dụng field cost
        work_documents: values.work_documents || null, // Mã hồ sơ tác nghiệp
        performed_by_employee_id: values.performed_by_employee_id || null,
        performed_by_supplier_id: values.performed_by_supplier_id || null,
        status: 'completed', // Tự động set trạng thái hoàn thành
      };

      if (editMode && initialData) {
        // Chế độ chỉnh sửa
        await apiClient.updateAssetWorkRecord(initialData.id, workRecordData);
        message.success('Cập nhật tác nghiệp thành công!');
      } else {
        // Chế độ tạo mới
        await apiClient.createAssetWorkRecord(workRecordData);
        message.success('Ghi nhận tác nghiệp thành công!');
        form.resetFields();
      }
      
      onSuccess();
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || (editMode ? 'Cập nhật tác nghiệp thất bại!' : 'Ghi nhận tác nghiệp thất bại!');
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };



  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={editMode && initialData ? {
          completion_date: dayjs(initialData.completed_date || initialData.work_date),
          maintenance_type_id: initialData.maintenance_type_id,
          work_description: initialData.work_description,
          cost: initialData.cost || 0,
          performed_by_employee_id: initialData.performed_by_employee_id,
          performed_by_supplier_id: initialData.performed_by_supplier_id,
          notes: initialData.notes,
          work_documents: initialData.work_documents,
        } : { 
          completion_date: dayjs(), 
          cost: 0 
        }}
        style={{ padding: '16px 0' }}
      >
      <Form.Item
        label="Ngày hoàn thành"
        name="completion_date"
        rules={[{ required: true, message: 'Vui lòng chọn ngày hoàn thành' }]}
      >
        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
      </Form.Item>
      
      <Form.Item
        label="Loại tác nghiệp"
        name="maintenance_type_id"
        rules={[{ required: true, message: 'Vui lòng chọn loại tác nghiệp' }]}
      >
        <Select
          showSearch
          optionFilterProp="children"
          options={maintenanceTypes.map(mt => ({ value: mt.id, label: mt.name }))}
          placeholder="Chọn loại tác nghiệp đã thực hiện"
        />
      </Form.Item>
      
      <Form.Item
        label="Mô tả công việc đã thực hiện"
        name="work_description"
        rules={[{ required: true, message: 'Vui lòng nhập mô tả công việc đã thực hiện' }]}
      >
        <Input.TextArea 
          rows={3} 
          placeholder="Mô tả chi tiết công việc đã thực hiện, các bước đã làm, kết quả đạt được..."
        />
      </Form.Item>
      
      <Form.Item
        label="Chi phí thực tế (VNĐ)"
        name="cost"
        rules={[{ required: true, message: 'Vui lòng nhập chi phí thực tế' }]}
      >
        <InputNumber 
          style={{ width: '100%' }} 
          min={0} 
          step={1000}
          formatter={value => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
          parser={(value: any) => value ? value.replace(/\D/g, '') : undefined}
        />
      </Form.Item>
      
      <Form.Item 
        label="Người thực hiện" 
        name="performed_by_employee_id"
        rules={[{ required: true, message: 'Vui lòng chọn người thực hiện' }]}
      >
        <Select
          showSearch
          optionFilterProp="children"
          placeholder="Chọn nhân viên thực hiện"
          options={filteredEmployees.map(emp => ({ value: emp.id, label: emp.full_name }))}
        />
      </Form.Item>
      
      <Form.Item 
        label="Nhà cung cấp thực hiện (tùy chọn)" 
        name="performed_by_supplier_id"
      >
        <Select
          allowClear
          showSearch
          optionFilterProp="children"
          placeholder="Chọn nhà cung cấp thực hiện (nếu có)"
          dropdownRender={(menu) => (
            <div>
              {menu}
              <div style={{ padding: '8px', borderTop: '1px solid #e8e8e8' }}>
                <Button
                  type="text"
                  icon={<span>+</span>}
                  onClick={() => setSupplierModalVisible(true)}
                  style={{ width: '100%', textAlign: 'left' }}
                >
                  + Tạo nhà cung cấp mới
                </Button>
              </div>
            </div>
          )}
        >
          {currentSupplierList.map(sup => (
            <Select.Option key={sup.id} value={sup.id}>
              {sup.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      
      <Form.Item 
        label="Ghi chú bổ sung" 
        name="notes"
      >
        <Input.TextArea 
          rows={2} 
          placeholder="Ghi chú về tình trạng tài sản sau tác nghiệp, vấn đề phát sinh, khuyến nghị..."
        />
      </Form.Item>

      <Form.Item 
        label="Mã hồ sơ tác nghiệp" 
        name="work_documents"
        rules={[{ required: true, message: 'Vui lòng nhập mã hồ sơ tác nghiệp' }]}
      >
        <Input 
          placeholder="Nhập mã hồ sơ tác nghiệp"
          allowClear
        />
      </Form.Item>
      
      <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
        <Button onClick={onCancel} style={{ marginRight: 8 }}>
          Hủy
        </Button>
        <Button type="primary" htmlType="submit" loading={loading}>
          {editMode ? 'Cập nhật' : 'Ghi nhận'}
        </Button>
              </Form.Item>
      </Form>

      <SupplierModal
        visible={supplierModalVisible}
        onCancel={() => setSupplierModalVisible(false)}
        onSuccess={handleSupplierCreated}
      />
    </>
  );
};

export default AssetWorkRecordForm; 